import { z } from 'zod'
import { Locale } from '../locales'

declare global {
  interface Window {
    embeddedservice_bootstrap: {
      settings: {
        language: string
        hideChatButtonOnLoad: boolean
        chatButtonPosition: string
      }
      init: (orgId: string, siteName: string, url: string, options: { scrt2URL: string }) => void
      utilAPI: {
        showChatButton: () => void
        hideChatButton: () => void
        launchChat: () => Promise<void>
        removeAllComponents: () => void
      }
      prechatAPI: {
        setHiddenPrechatFields: (fields: Record<string, string>) => void
      }
    }
  }
}

let salesforceMessagingPromise: Promise<typeof window.embeddedservice_bootstrap> | null = null
const SALESFORCE_MESSAGING_LOCALES: Record<Locale, string> = {
  de: 'de',
  en: 'en_US',
  nl: 'nl_NL'
}
const SALESFORCE_MESSAGE_LOCALES_REVERSE: Record<string, Locale> = Object.fromEntries(Object.entries(SALESFORCE_MESSAGING_LOCALES).map(([k, v]) => [v, k])) as Record<string, Locale>

export function getCurrentSalesforceMessagingLocale (): Locale | null {
  const locale: string | undefined = window.embeddedservice_bootstrap?.settings?.language
  if (locale == null) {
    return null
  }
  return SALESFORCE_MESSAGE_LOCALES_REVERSE[locale] ?? null
}

export async function unloadSalesforceMessaging (): Promise<void> {
  if (salesforceMessagingPromise == null) {
    return
  }

  void salesforceMessagingPromise.finally(() => {
    window.embeddedservice_bootstrap.utilAPI.removeAllComponents()
    document.getElementById('salesforce-web-messaging-script')?.remove()
  })
  salesforceMessagingPromise = null
  await Promise.allSettled([salesforceMessagingPromise])
}

// eslint-disable-next-line @typescript-eslint/promise-function-async
export function loadSalesforceMessaging (locale: Locale): Promise<typeof window.embeddedservice_bootstrap> {
  salesforceMessagingPromise ??= new Promise((resolve, reject) => {
    try {
      const script = document.createElement('script')
      const url = z.url().parse(import.meta.env.VITE_SALESFORCE_MESSAGING_URL)
      script.onerror = (err) => reject(err)
      script.onload = () => {
        try {
          const orgId = z.string().parse(import.meta.env.VITE_SALESFORCE_MESSAGING_ORG_ID)
          const siteName = z.string().parse(import.meta.env.VITE_SALESFORCE_MESSAGING_SITE_NAME)
          const scrt2URL = z.url().parse(import.meta.env.VITE_SALESFORCE_MESSAGING_SCRT2_URL)
          window.embeddedservice_bootstrap.settings.language = SALESFORCE_MESSAGING_LOCALES[locale]
          window.embeddedservice_bootstrap.settings.hideChatButtonOnLoad = true
          window.embeddedservice_bootstrap.init(
            orgId,
            siteName,
            url,
            { scrt2URL }
          )
          console.log('Salesforce Web Messaging loaded')
          resolve(window.embeddedservice_bootstrap)
        } catch (e) {
          console.error('Failed to load Salesforce Web Messaging', e)
          reject(e)
        }
      }
      script.src = `${url}/assets/js/bootstrap.min.js`
      script.id = 'salesforce-web-messaging-script'
      document.head.appendChild(script)
    } catch (e) {
      reject(e)
    }
  })
  return salesforceMessagingPromise
}
